/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `reportOptionFilters` for the Caintra Connect frontend.
 * @module logic/reports/reportOptionFilters
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { getAllBusinessEvents } from "../businessEvents";
import { getAllB2BEvents } from "../b2bEvents";
import { getCompaniesNames } from "../company";
import { getBuyerCompaniesBasicInfo } from "../buyerCompany";
import { getActiveEventsData } from "./index.js";

const mapCompaniesWithSearch = (companies, searchField = "nombreComercial") =>
  companies.map((company) => ({
    ...company,
    search: company[searchField].toLowerCase(),
  }));

export const loadTablesReportEvents = async ({
  navigate,
  setEventsOptions,
  setLoadingEvents,
}) => {
  setLoadingEvents(true);
  try {
    const allEvents = await getAllBusinessEvents(
      localStorage.getItem("userId"),
      navigate,
    );
    setEventsOptions(allEvents);
  } catch (err) {
    logger.error("Error", { err: sanitizeError(err) });
    setEventsOptions([]);
  } finally {
    setLoadingEvents(false);
  }
};

export const loadLoginReportMemberCompanies = async ({
  navigate,
  memberCompaniesOptions,
  setMemberCompaniesOptions,
  setLoadingMemberCompanies,
}) => {
  if (memberCompaniesOptions?.length > 0) {
    return;
  }

  setLoadingMemberCompanies(true);
  try {
    const companiesNames = await getCompaniesNames(navigate);
    setMemberCompaniesOptions(mapCompaniesWithSearch(companiesNames));
  } catch (err) {
    logger.error("Error", { err: sanitizeError(err) });
    setMemberCompaniesOptions([]);
  } finally {
    setLoadingMemberCompanies(false);
  }
};

export const loadBuyerReportFilters = async ({
  navigate,
  eventsOptions,
  allBuyerCompanies,
  setEventsOptions,
  setLoadingEvents,
  setAllBuyerCompanies,
  setBuyerCompaniesOptions,
  setLoadingBuyerCompanies,
}) => {
  if (!eventsOptions?.length) {
    setLoadingEvents(true);
    try {
      const allEvents = await getAllBusinessEvents(
        localStorage.getItem("userId"),
        navigate,
      );
      setEventsOptions(allEvents);
    } catch (err) {
      logger.error("Error", { err: sanitizeError(err) });
      setEventsOptions([]);
    } finally {
      setLoadingEvents(false);
    }
  }

  if (!allBuyerCompanies?.length) {
    setLoadingBuyerCompanies(true);
    try {
      const buyerCompanies = await getBuyerCompaniesBasicInfo(navigate);
      const searchableBuyerCompanies = mapCompaniesWithSearch(buyerCompanies);
      setAllBuyerCompanies(searchableBuyerCompanies);
      setBuyerCompaniesOptions(searchableBuyerCompanies);
    } catch (err) {
      logger.error("Error", { err: sanitizeError(err) });
      setAllBuyerCompanies([]);
      setBuyerCompaniesOptions([]);
    } finally {
      setLoadingBuyerCompanies(false);
    }
  }
};

export const loadB2bAttendanceReportEvents = async ({
  navigate,
  b2bEventsOptions,
  setB2bEventsOptions,
  setLoadingB2bEvents,
}) => {
  if (b2bEventsOptions?.length > 0) {
    return;
  }

  setLoadingB2bEvents(true);
  try {
    const allB2bEvents = await getAllB2BEvents(navigate);
    setB2bEventsOptions(allB2bEvents);
  } catch (err) {
    logger.error("Error", { err: sanitizeError(err) });
    setB2bEventsOptions([]);
  } finally {
    setLoadingB2bEvents(false);
  }
};

export const resetAppointmentDetailReportFilters = ({
  setEventsOptions,
  setSelectedEvent,
}) => {
  setEventsOptions([]);
  setSelectedEvent(null);
};

export const resetGeneralB2bReportFilters = ({
  setB2bEventsOptions,
  setSelectedB2bEvent,
}) => {
  setB2bEventsOptions([]);
  setSelectedB2bEvent(null);
};

export const loadInvoiceReportEvents = async ({
  navigate,
  allEventsOptions,
  setAllEventsOptions,
  setLoadingEvents,
}) => {
  if (allEventsOptions?.length > 0) {
    return;
  }

  setLoadingEvents(true);
  try {
    await getActiveEventsData(navigate, setAllEventsOptions);
  } catch (err) {
    logger.error("Error", { err: sanitizeError(err) });
    setAllEventsOptions([]);
  } finally {
    setLoadingEvents(false);
  }
};

/**
 * Loads filter options for the selected report type.
 * @param {number} optionValue - Selected report option value
 * @param {Object} deps - State setters and current options from Reports page
 */
export const loadFiltersForReportOption = async (optionValue, deps) => {
  switch (optionValue) {
    case 0:
      await loadTablesReportEvents(deps);
      break;
    case 1:
      await loadLoginReportMemberCompanies(deps);
      break;
    case 2:
      await loadBuyerReportFilters(deps);
      break;
    case 3:
      await loadB2bAttendanceReportEvents(deps);
      break;
    case 7:
    case 9:
      resetAppointmentDetailReportFilters(deps);
      break;
    case 12:
      resetGeneralB2bReportFilters(deps);
      break;
    case 4:
    case 5:
      await loadInvoiceReportEvents(deps);
      break;
    default:
      // Unsupported report option. No filter preload required.
      break;
  }
};
